import { cn } from "@/lib/cn";
import { formatAbsolute } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Bot, User, FileEdit, AlertTriangle } from "lucide-react";

export interface MessageRow {
  id: string;
  channel: string;
  direction: "inbound" | "outbound";
  actor: "contact" | "ai" | "human" | "system";
  text: string | null;
  media_url: string | null;
  media_type: string | null;
  source_listing: string | null;
  draft: number;
  created_at: string;
  external_message_id: string;
}

/**
 * Where in a same-actor consecutive run this bubble sits. Drives WhatsApp-
 * style grouping: only the first bubble in a run shows the actor label,
 * only the last one shows the timestamp, and middle bubbles get tighter
 * corners on the run-side edge.
 */
export type BubblePosition = "only" | "first" | "middle" | "last";

export function MessageBubble({
  message,
  position = "only",
}: {
  message: MessageRow;
  position?: BubblePosition;
}) {
  const isInbound = message.direction === "inbound";
  const isDraft = message.draft === 1;
  const isAi = message.actor === "ai";
  const isHuman = message.actor === "human";
  const showHeader = position === "only" || position === "first";
  const showTimestamp = position === "only" || position === "last";
  const notSent = message.external_message_id?.startsWith("synthetic:");

  // WhatsApp-style: keep the "run-side" corner sharp on consecutive bubbles
  // so the group reads as one thought. Outer corners stay rounded.
  const roundedCorners = isInbound
    ? cn(
        position === "first" && "rounded-bl-sm",
        position === "middle" && "rounded-l-sm",
        position === "last" && "rounded-tl-sm",
      )
    : cn(
        position === "first" && "rounded-br-sm",
        position === "middle" && "rounded-r-sm",
        position === "last" && "rounded-tr-sm",
      );

  return (
    <div className={cn("flex", isInbound ? "justify-start" : "justify-end")}>
      <div className={cn("max-w-[78%]", isInbound ? "" : "items-end")}>
        {(showHeader || isDraft || notSent) && (
          <div className="flex items-center gap-1.5 mb-1 text-[10px] text-slate-400">
            {showHeader && (
              <>
                {isAi && <Bot className="h-3 w-3" />}
                {isHuman && <User className="h-3 w-3" />}
                <span className="uppercase tracking-wider font-semibold">
                  {isInbound ? "Contact" : message.actor}
                </span>
              </>
            )}
            {isDraft && (
              <Badge variant="warning" className="!py-0">
                <FileEdit className="h-3 w-3" /> draft
              </Badge>
            )}
            {notSent && (
              <Badge variant="muted" className="!py-0">
                <AlertTriangle className="h-3 w-3" /> not sent
              </Badge>
            )}
          </div>
        )}
        <div
          className={cn(
            "rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm",
            roundedCorners,
            isInbound
              ? "bg-brand-surface border border-slate-200 text-slate-900"
              : isDraft
              ? "bg-amber-50 border border-amber-200 text-amber-900"
              : isAi
              ? "bg-slate-900 text-white"
              : "bg-emerald-600 text-white",
          )}
        >
          {message.text || (
            <span className="italic text-slate-400">
              [{message.media_type ?? "no text"}]
            </span>
          )}
          {message.media_url && (
            <div className="mt-2">
              <a
                href={message.media_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs underline opacity-80"
              >
                media attachment ↗
              </a>
            </div>
          )}
        </div>
        {showTimestamp && (
          <div
            className={cn(
              "mt-0.5 text-[10px] text-slate-400",
              isInbound ? "text-left" : "text-right",
            )}
          >
            {formatAbsolute(message.created_at)}
          </div>
        )}
      </div>
    </div>
  );
}
